import {
  IconBrandSpeedtest,
  IconClockBolt,
  IconClockCog,
  IconWindow,
} from '@tabler/icons-react'
import ReactEcharts from 'echarts-for-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ContentCard,
  MetricCard,
  ScalrBoxView,
  ScalrCarousel,
} from '@/components/customComponents'
import { MasterChart } from '@/components/ui/charts'
import { DataTable } from '@/components/ui/data-table'

import KPIGoalChart from '@/components/ui/charts/KpiGoalChart'
import { stackedBarChartOptions } from '@/constants/CHART_CONSTANTS'
import { metricCardColors } from '@/constants/COLOR_PALETTE'
import { toast, useSpeedMetrics, useSpeedRegionBoxMetrics } from '@/hooks'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { KpiGoalsModel, SpeedOfServiceMetricsModel } from '@/types'
import {
  calculateRemainingDaysInQuarter,
  calculateTrailingGoalData,
  dateToString,
  groupedSpeedOfServiceColumns,
  type DateType,
} from '@/utils'

export default function SpeedOfServicePage({
  kpiGoals,
  kpiGoalsLoading,
}: {
  kpiGoals: KpiGoalsModel
  kpiGoalsLoading: boolean
}) {
  const { t } = useTranslation()
  const { selectedBranch, date, dateFilterType } =
    useReduxBaseFilter('speed-of-service')
  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('speed-of-service')
  )

  const [isViewMore, setViewMore] = useState(false)

  const handleViewMore = () => {
    setViewMore(!isViewMore)
  }

  const {
    branchData,
    companyData,
    metricCardData,
    loading,
    error: speedMetricsError,
    refetch,
  } = useSpeedMetrics(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType as DateType,
    selectedBranch
  )

  const {
    processedData,
    loading: regionCardsLoading,
    error: regionCardsError,
    refetch: regionCardsRefetch,
  } = useSpeedRegionBoxMetrics(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType as DateType
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing speed of service page data...')
      refetch()
      regionCardsRefetch()
    }
  }, [refreshTrigger, refetch, regionCardsRefetch])

  // Show error toast if there is an error
  useEffect(() => {
    if (speedMetricsError || regionCardsError) {
      toast({
        title: t('common.errors.serverError'),
        description: speedMetricsError?.message || regionCardsError?.message,
        variant: 'destructive',
      })
    }
  }, [speedMetricsError, regionCardsError])

  const [trailingGoalData, setTrailingGoalData] = useState<number>(0)
  const [remainingDaysInQuarter, setRemainingDaysInQuarter] =
    useState<number>(0)
  useEffect(() => {
    if (
      companyData?.speedOfService &&
      kpiGoals?.speedOfService?.speedOfService
    ) {
      setRemainingDaysInQuarter(calculateRemainingDaysInQuarter())
      setTrailingGoalData(
        calculateTrailingGoalData(
          companyData.speedOfService,
          kpiGoals?.speedOfService?.speedOfService,
          'speed-of-service'
        )
      )
    }
  }, [companyData?.speedOfService, kpiGoals?.speedOfService?.speedOfService])

  return (
    <div className="space-y-6">
      <div className="space-y-3 mb-10">
        <ScalrCarousel>
          <MetricCard
            title={t('clarity.metrics.speedOfService')}
            mtdCurrContent={metricCardData?.currMetricsMonthly?.speedOfService}
            ytdCurrContent={metricCardData?.currMetricsYearly?.speedOfService}
            mtdPrevContent={metricCardData?.prevMetricsMonthly?.speedOfService}
            ytdPrevContent={metricCardData?.prevMetricsYearly?.speedOfService}
            content={companyData?.speedOfService}
            type="seconds"
            kpiGoal={kpiGoals?.speedOfService?.speedOfService}
            loading={loading || kpiGoalsLoading}
            icon={<IconBrandSpeedtest color="white" size={20} />}
            color={metricCardColors.blue500}
            isIncreasingPositive={false}
          />
          <MetricCard
            title={t('clarity.metrics.utilization')}
            mtdCurrContent={metricCardData?.currMetricsMonthly?.utilization}
            ytdCurrContent={metricCardData?.currMetricsYearly?.utilization}
            mtdPrevContent={metricCardData?.prevMetricsMonthly?.utilization}
            ytdPrevContent={metricCardData?.prevMetricsYearly?.utilization}
            content={companyData?.utilization}
            type="percentage"
            kpiGoal={kpiGoals?.speedOfService?.utilization}
            loading={loading || kpiGoalsLoading}
            icon={<IconClockCog color="white" size={20} />}
            color={metricCardColors.red500}
          />
          <MetricCard
            title={t('clarity.metrics.serviceTime')}
            mtdCurrContent={metricCardData?.currMetricsMonthly?.serviceTotal}
            ytdCurrContent={metricCardData?.currMetricsYearly?.serviceTotal}
            mtdPrevContent={metricCardData?.prevMetricsMonthly?.serviceTotal}
            ytdPrevContent={metricCardData?.prevMetricsYearly?.serviceTotal}
            content={companyData?.serviceTotal}
            type="hour"
            kpiGoal={kpiGoals?.speedOfService?.serviceTime}
            loading={loading || kpiGoalsLoading}
            icon={<IconClockBolt color="white" size={20} />}
            color={metricCardColors.green500}
            isIncreasingPositive={false}
          />
          <MetricCard
            title={t('clarity.metrics.windowTime')}
            mtdCurrContent={metricCardData?.currMetricsMonthly?.windowTotal}
            ytdCurrContent={metricCardData?.currMetricsYearly?.windowTotal}
            mtdPrevContent={metricCardData?.prevMetricsMonthly?.windowTotal}
            ytdPrevContent={metricCardData?.prevMetricsYearly?.windowTotal}
            content={companyData?.windowTotal}
            type="hour"
            kpiGoal={kpiGoals?.speedOfService?.windowTime}
            loading={loading || kpiGoalsLoading}
            icon={<IconWindow color="white" size={20} />}
            color={metricCardColors.orange500}
            isIncreasingPositive={false}
          />
        </ScalrCarousel>
      </div>

      {isViewMore ? (
        <DataTable
          columns={groupedSpeedOfServiceColumns}
          data={companyData ? [companyData, ...branchData] : []}
          selectedBranch={selectedBranch}
          cardTitle={t('clarity.speed.title')}
          cardDescription={t('clarity.speed.breakdown')}
          tableViewOption={true}
          loading={loading}
          onClose={handleViewMore}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ContentCard
              title={t('clarity.speed.timesByShifts')}
              onViewMore={handleViewMore}
              loading={loading}
              isEmpty={!companyData}
            >
              {companyData && (
                <ReactEcharts option={getStackedBarChartData(companyData)} />
              )}
            </ContentCard>

            <ContentCard
              title={t('clarity.speed.utilizationByShifts')}
              onViewMore={handleViewMore}
              loading={loading}
              isEmpty={!companyData}
            >
              {companyData && (
                <ReactEcharts option={getStackedTipBarChartData(companyData)} />
              )}
            </ContentCard>

            <ContentCard
              title={t('clarity.speed.sosVsKpiGoal')}
              loading={loading || kpiGoalsLoading}
            >
              <KPIGoalChart
                trailingGoalData={trailingGoalData}
                currentData={companyData?.speedOfService || 0}
                kpiGoalData={kpiGoals?.speedOfService?.speedOfService || 0}
                unit="number"
                remainingDaysInQuarter={remainingDaysInQuarter}
              />
            </ContentCard>
          </div>

          <ScalrBoxView
            data={processedData}
            loading={regionCardsLoading}
            filterValues={{
              start: dateToString(date, 'from'),
              end: dateToString(date, 'to'),
            }}
          />
        </>
      )}

      <MasterChart
        chartType="speedOfService"
        dateRange={date}
        selectedBranchFilter={selectedBranch}
        chartTitle={t('clarity.charts.speedOfServiceChart')}
      />
    </div>
  )
}

// TODO: to be moved to a separate file
const getStackedBarChartData = (data?: SpeedOfServiceMetricsModel) => {
  if (!data) return {}
  const rawData = [
    [data.windowEarlyAM, data.windowAM, data.windowMid, data.windowPM],
    [data.serviceEarlyAM, data.serviceAM, data.serviceMid, data.servicePM],
  ]

  return stackedBarChartOptions(
    rawData,
    ['Window Time', 'Service Time'],
    ['Early AM', 'AM', 'MID', 'PM'],
    '60%',
    'hour'
  )
}

const getStackedTipBarChartData = (data?: SpeedOfServiceMetricsModel) => {
  const seriesData = data?.utilizationByShift
  if (!seriesData) return {}

  return stackedBarChartOptions(
    seriesData,
    ['Utilization'],
    ['Early AM', 'AM', 'MID', 'PM'],
    '60%',
    'percentage'
  )
}
